import { z } from 'zod';
import { isoUtcDateTimeSchema } from '../shared/schemas.js';
import { actionIdSchema } from '../action/action.schemas.js';

export const operationIdSchema = z.coerce.number().int().positive();

export const operationStatusCodeSchema = z.enum(['DRAFT', 'VALIDATED', 'STARTED', 'COMPLETE']);
export type OperationStatusCode = z.infer<typeof operationStatusCodeSchema>;

export const operationCodeNameSchema = z
  .string()
  .trim()
  .min(1, 'Le nom de code est obligatoire.')
  .max(100, 'Le nom de code ne peut pas dépasser 100 caractères.');

export const createOperationSchema = z
  .object({
    codeName: operationCodeNameSchema,
    startsAt: isoUtcDateTimeSchema.nullable().optional(),
  })
  .strict();

export type CreateOperationInput = z.infer<typeof createOperationSchema>;

export const updateOperationSchema = z
  .object({
    codeName: operationCodeNameSchema.optional(),
    startsAt: isoUtcDateTimeSchema.nullable().optional(),
  })
  .strict();

export type UpdateOperationInput = z.infer<typeof updateOperationSchema>;

export const setOperationActionsSchema = z
  .object({
    actionIds: z.array(actionIdSchema),
  })
  .strict();

export type SetOperationActionsInput = z.infer<typeof setOperationActionsSchema>;

export const operationSchema = z.object({
  id: z.number().int().positive(),
  publicId: z.string(),
  codeName: z.string(),
  statusCode: operationStatusCodeSchema,
  startsAt: isoUtcDateTimeSchema.nullable(),
  completedAt: isoUtcDateTimeSchema.nullable(),
  winnerAgentId: z.number().int().positive().nullable(),
  createdByUserId: z.number().int().positive(),
  archivedAt: isoUtcDateTimeSchema.nullable(),
  createdAt: isoUtcDateTimeSchema,
  updatedAt: isoUtcDateTimeSchema,
});

export type Operation = z.infer<typeof operationSchema>;

export const operationListSchema = z.object({
  operations: z.array(operationSchema),
});

export type OperationList = z.infer<typeof operationListSchema>;

export const operationActionSchema = z.object({
  operationId: z.number().int().positive(),
  actionId: z.number().int().positive(),
  isActive: z.boolean(),
  isReassignable: z.boolean(),
  deactivatedAt: isoUtcDateTimeSchema.nullable(),
  description: z.string(),
  /** Action personnalisée, propre à cette opération (TD-026). */
  isCustom: z.boolean(),
});

export type OperationAction = z.infer<typeof operationActionSchema>;

export const operationActionListSchema = z.object({
  operationActions: z.array(operationActionSchema),
});

export type OperationActionList = z.infer<typeof operationActionListSchema>;

/** Résultat d'une activation ou désactivation en masse (TD-026). */
export const operationActionBulkResultSchema = z.object({
  operationActions: z.array(operationActionSchema),
  /** Actions dont l'état a changé. */
  changedCount: z.number().int().nonnegative(),
});

export type OperationActionBulkResult = z.infer<typeof operationActionBulkResultSchema>;